// Core
import PropTypes from "prop-types";

// Components
import { Box, Typography } from "@mui/material";

/**
 * Tokenises a formula string and renders it with syntax highlighting.
 * Colours (dark/light) are handled via MUI sx theme callbacks.
 */
function FormulaLine({ line }) {
  // Tokenise: split on operators, parens, spaces — preserving delimiters
  const tokens = line.split(/([+\-*/=()]|\s+)/);

  return (
    <Box component="span" sx={{ display: "block", lineHeight: 1.8, wordBreak: "break-word" }}>
      {tokens.map((token, i) => {
        if (!token) return null;

        // whitespace — preserve
        if (/^\s+$/.test(token)) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i}>{token}</span>
          );
        }

        // operator
        if (/^[+\-*/]$/.test(token)) {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{ color: (theme) => (theme.palette.mode === "dark" ? "#f6ad55" : "#d97706") }}
            >
              {token}
            </Box>
          );
        }

        // equals sign
        if (token === "=") {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                mx: 0.25,
              }}
            >
              {token}
            </Box>
          );
        }

        // parentheses
        if (/^[()]$/.test(token)) {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
              }}
            >
              {token}
            </Box>
          );
        }

        // standalone number
        if (/^\d+(\.\d+)?$/.test(token)) {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{ color: (theme) => (theme.palette.mode === "dark" ? "#68d391" : "#276749") }}
            >
              {token}
            </Box>
          );
        }

        // K-coefficient (K1, K2, K5, K6…)
        if (/^K\d+$/.test(token)) {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{ color: (theme) => (theme.palette.mode === "dark" ? "#b794f4" : "#6b21a8") }}
            >
              {token}
            </Box>
          );
        }

        // Single-letter multiplier (A, B, C…)
        if (/^[A-Z]$/.test(token)) {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{
                color: (theme) => (theme.palette.mode === "dark" ? "#fbd38d" : "#b45309"),
                fontWeight: 700,
              }}
            >
              {token}
            </Box>
          );
        }

        // variable / named term (contains underscore or mixed case)
        if (/[_A-Z]/.test(token) && /[a-zA-Z_]/.test(token)) {
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              component="span"
              sx={{ color: (theme) => (theme.palette.mode === "dark" ? "#76e4f7" : "#0369a1") }}
            >
              {token}
            </Box>
          );
        }

        // default
        // eslint-disable-next-line react/no-array-index-key
        return <span key={i}>{token}</span>;
      })}
    </Box>
  );
}

FormulaLine.propTypes = {
  line: PropTypes.string.isRequired,
};

function FormulaBlock({ title, body }) {
  const lines = body.split("\n").filter(Boolean);

  return (
    <Box
      mt={3}
      p={2}
      sx={{
        borderRadius: 1,
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.03)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.10)"
            : "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          fontWeight: 700,
          letterSpacing: "0.08em",
          display: "block",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          fontFamily: "monospace",
          fontSize: "0.8rem",
          color: (theme) => (theme.palette.mode === "dark" ? "#e2e8f0" : "#1a202c"),
        }}
      >
        {lines.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <FormulaLine key={i} line={line} />
        ))}
      </Box>
    </Box>
  );
}

FormulaBlock.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
};

export default FormulaBlock;
